import { NavLink, useNavigate } from 'react-router-dom';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useAuth } from '../../contexts/AuthContext';
import type { UserRole } from '../../types/auth';

interface NavItem {
  label: string;
  icon: string;
  path: string;
  roles?: UserRole[];
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', icon: 'dashboard', path: '/dashboard' },
      { label: 'Pacientes', icon: 'group', path: '/pacientes' },
      { label: 'Presupuestos', icon: 'description', path: '/presupuestos' },
      { label: 'Seguimiento', icon: 'follow_the_signs', path: '/seguimiento-presupuestos' },
      { label: 'Recordatorios', icon: 'notifications', path: '/recordatorios' },
    ],
  },
  {
    title: 'Catálogos',
    items: [
      { label: 'Catálogo del Centro', icon: 'inventory_2', path: '/catalogos/centro', roles: ['GESTOR_SEDE', 'ADMIN'] },
      { label: 'Tratamientos', icon: 'medical_services', path: '/catalogos/tratamientos', roles: ['GESTOR_SEDE', 'ADMIN'] },
      { label: 'Plantillas', icon: 'content_copy', path: '/catalogos/plantillas', roles: ['GESTOR_SEDE', 'ADMIN'] },
      { label: 'Descuentos', icon: 'percent', path: '/catalogos/descuentos', roles: ['GESTOR_SEDE', 'ADMIN'] },
    ],
  },
  {
    title: 'Catálogos Maestros',
    items: [
      { label: 'Especialidades', icon: 'category', path: '/admin/especialidades', roles: ['ADMIN'] },
      { label: 'Subespecialidades', icon: 'account_tree', path: '/admin/subespecialidades', roles: ['ADMIN'] },
      { label: 'Grupos Tratamiento', icon: 'folder_special', path: '/admin/grupos-tratamiento', roles: ['ADMIN'] },
      { label: 'Actos Médicos', icon: 'stethoscope', path: '/admin/actos-medicos', roles: ['ADMIN'] },
      { label: 'Sociedades', icon: 'corporate_fare', path: '/admin/sociedades', roles: ['ADMIN'] },
      { label: 'Tipos de Rechazo', icon: 'block', path: '/admin/tipos-rechazo', roles: ['ADMIN'] },
      { label: 'Tipos de Documento', icon: 'attach_file', path: '/admin/tipos-documento', roles: ['ADMIN'] },
      { label: 'Tipos de Contacto', icon: 'phone_callback', path: '/admin/tipos-contacto', roles: ['ADMIN'] },
      { label: 'Plantillas Recordatorio', icon: 'edit_notifications', path: '/admin/plantillas-recordatorio', roles: ['ADMIN'] },
    ],
  },
  {
    title: 'Tarifas y Precios',
    items: [
      { label: 'Aseguradoras', icon: 'health_and_safety', path: '/admin/aseguradoras', roles: ['ADMIN'] },
      { label: 'Pólizas', icon: 'policy', path: '/admin/polizas', roles: ['ADMIN'] },
      { label: 'Tarifas', icon: 'receipt_long', path: '/admin/tarifas', roles: ['ADMIN'] },
    ],
  },
  {
    title: 'Administración',
    items: [
      { label: 'Usuarios', icon: 'manage_accounts', path: '/admin/usuarios', roles: ['ADMIN'] },
      { label: 'Sedes', icon: 'location_on', path: '/admin/sedes', roles: ['ADMIN'] },
      { label: 'Empresa', icon: 'business', path: '/admin/empresa', roles: ['ADMIN'] },
      { label: 'Configuración', icon: 'settings', path: '/configuracion', roles: ['GESTOR_SEDE', 'ADMIN'] },
      { label: 'WhatsApp', icon: 'chat', path: '/admin/whatsapp', roles: ['GESTOR_SEDE', 'ADMIN'] },
      { label: 'Salud del sistema', icon: 'monitoring', path: '/admin/sistema', roles: ['ADMIN'] },
    ],
  },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { requestNavigation } = useUnsavedChanges();

  // Filtrar items según rol del usuario
  const filterByRole = (items: NavItem[]): NavItem[] => {
    if (!user) return [];
    return items.filter(item => {
      if (!item.roles) return true;
      return item.roles.includes(user.role);
    });
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col h-screen">
      {/* Logo */}
      <div className="p-6 flex items-center gap-3">
        <img
          src="/icon-192.png"
          srcSet="/icon-192.png 1x, /icon-512.png 2x"
          alt="IMI Gestión"
          width={40}
          height={40}
          className="w-10 h-10 rounded-[18%] shadow-primary flex-shrink-0"
        />
        <div>
          <h1 className="text-base font-bold leading-tight text-slate-900">IMI Gestión</h1>
          <p className="text-[10px] text-primary uppercase tracking-widest font-semibold">
            Premium Dental
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-6 overflow-y-auto custom-scrollbar">
        {navigation.map((section, sectionIndex) => {
          const filteredItems = filterByRole(section.items);
          if (filteredItems.length === 0) return null;

          return (
            <div key={sectionIndex}>
              {section.title && (
                <p className="px-3 pb-2 text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {filteredItems.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      requestNavigation(() => navigate(item.path));
                    }}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200
                      ${isActive
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }
                    `}
                  >
                    <span className="material-symbols-outlined text-xl">
                      {item.icon}
                    </span>
                    <span className="text-sm">{item.label}</span>
                  </NavLink>
                ))}
            </div>
            </div>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-[10px] text-slate-500 truncate">
              {user?.role === 'ADMIN' && 'Administrador'}
              {user?.role === 'GESTOR_SEDE' && 'Gestor de Sede'}
              {user?.role === 'USER' && 'Usuario'}
            </p>
          </div>

          {/* Logout button */}
          <button
            onClick={handleLogout}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Cerrar sesión"
          >
            <span className="material-symbols-outlined text-xl">logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
